import React from 'react'
import styled from 'styled-components'

import { pipe, Result } from '@blade/std-lib'
import {
  WarningTriangleIcon,
  CircleErrorIcon,
  Refresh2Icon,
} from '@root/shared/ui-kit'

import * as UpdateLevel_ from '../../model/update-level'
import * as UpdateState from '../../model/update-state'

import type { PlatformUpdate } from '@root/shared'

const getIcon = (updateState: UpdateState.UpdateState) =>
  UpdateState.match.I(updateState)({
    LatestUpdate: () => undefined,
    UpdateCheck: () => <Refresh2Icon height={9} width={9} />,
    UpdateCheckError: () => <CircleErrorIcon height={9} width={9} />,
    UpdateAvailable: () => <WarningTriangleIcon height={9} width={9} />,
    UpdateDownloadError: () => <CircleErrorIcon height={9} width={9} />,
    UpdateReady: () => <WarningTriangleIcon height={9} width={9} />,
    UpdateInstallation: () => <WarningTriangleIcon height={9} width={9} />,
    UpdateInstallError: () => <CircleErrorIcon height={9} width={9} />,
    UpdateRelaunchError: () => <CircleErrorIcon height={9} width={9} />,
  })

const getUpdateLevelIntent: Record<UpdateLevel_.UpdateLevel, Intent> = {
  major: 'error',
  minor: 'warn',
  patch: 'warn',
  same: 'warn',
}

const getUpdateIntent = (update: PlatformUpdate) =>
  pipe(
    UpdateLevel_.fromUpdate(update),
    Result.match({
      onFailure: () => 'none' as Intent,
      onSuccess: (s) => getUpdateLevelIntent[s],
    }),
  )

const getIntent = (updateState: UpdateState.UpdateState): Intent =>
  UpdateState.match.I(updateState)({
    LatestUpdate: () => 'none' as const,
    UpdateCheck: () => 'none' as const,
    UpdateCheckError: () => 'error' as const,
    UpdateAvailable: ({ update }) => getUpdateIntent(update),
    UpdateDownloadError: () => 'error' as const,
    UpdateReady: ({ update }) => getUpdateIntent(update),
    UpdateInstallation: ({ update }) => getUpdateIntent(update),
    UpdateInstallError: () => 'error' as const,
    UpdateRelaunchError: () => 'error' as const,
  })

type Intent = 'warn' | 'error' | 'none'

const getColor: Record<Intent, string> = {
  warn: 'var(--warning-9)',
  error: 'var(--error-9)',
  none: 'inherit',
}

export const UpdateLevel = (props: {
  updateState: UpdateState.UpdateState
}) => {
  return (
    <Root $intent={getIntent(props.updateState)}>
      {getIcon(props.updateState)}
    </Root>
  )
}

const Root = styled.div<{ $intent: Intent }>`
  display: flex;
  color: ${(props) => getColor[props.$intent]};
`
